import {
  faChevronDown,
  faChevronUp
} from '@fortawesome//pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { chain, maxBy } from 'lodash';
import React, { useMemo, useState } from 'react';
import { ProjectGq } from '../../../../../graphql/projects/models/ProjectGq';
import { FrontendUserEventGq } from '../../../../../graphql/users/queries/useFrontendLogUserEventGq';
import { usersApiService } from '../../../../../graphql/users/usersApiService';
import { useDebounceValue } from '../../../../../logic/otherUtilities/useDebounceValue';
import VoxelButton from '../../../../voxel/Buttons/VoxelButton';
import VoxelInfo from '../../../../voxel/Info/VoxelInfo';
import VoxelAdvancedTextSearch from '../../../../voxel/Search/VoxelAdvancedTextSearch';
import VoxelText from '../../../../voxel/Text/VoxelText';
import { getColumnDescription } from './getColumnDescription';
import { getColumnId } from './getColumnId';
import { getColumnName } from './getColumnName';
import { KeywordTableColumn, KeywordTableColumnTypes } from './models';
import { KeywordColumnPickerBag } from './useKeywordColumnPicker';

export interface KeywordColumnPickerHiddenColumnsProps {
  keywordColumnPickerBag: KeywordColumnPickerBag;
  project: ProjectGq;
}

export const KeywordColumnPickerHiddenColumns: React.FC<KeywordColumnPickerHiddenColumnsProps> = ({
  keywordColumnPickerBag,
  project,
}) => {
  const { columnState, setColumnState, shownOrderedColumns } = keywordColumnPickerBag;
  const [searchState, setSearchState] = useState<string>('');
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>({});

  // Adding
  const logUserEventMutation = usersApiService.mutations.useLogFrontendUserEvent();
  const handleAdd = (addedColumn: KeywordTableColumn) => {
    logUserEventMutation({
      event: FrontendUserEventGq.TABLE_SLOW_ADD_COLUMN,
    });
    setColumnState((prev) => {
      const maxOrder = maxBy(prev, (c) => c.order)?.order ?? 0;
      return chain(prev)
        .map((c) => {
          if (c.id === addedColumn.id && c.type === addedColumn.type)
            return { ...c, order: maxOrder };
          return c;
        })
        .orderBy((c) => c === undefined || c.order)
        .map((c, index) => {
          if (c.order === undefined) return c;
          if (c.order === index) return c;
          return { ...c, order: index };
        })
        .value();
    });
  };

  // Hidden columns
  const debouncedSearchState = useDebounceValue(searchState, 150);
  const hiddenColumns = useMemo(
    () =>
      chain(columnState)
        .map((column) => ({ column, name: getColumnName(column, project) }))
        .filter(
          (c) =>
            c.column.order === undefined &&
            (!debouncedSearchState.length ||
              c.name.toLowerCase().includes(debouncedSearchState.toLowerCase()))
        )
        .orderBy((c) => c.name)
        .groupBy((c) => {
          if ((project?.hasEnglishTranslation || project?.translated) &&
            [
              KeywordTableColumnTypes.TranslationFrom,
              KeywordTableColumnTypes.EnglishTranslation,
              KeywordTableColumnTypes.EnglishTranslation,
              KeywordTableColumnTypes.TranslationFromVolume,
            ].includes(c.column.type)
          )
            return 'Translation';
          if (
            [
              KeywordTableColumnTypes.Volume,
              KeywordTableColumnTypes.VolumeTrend,
              KeywordTableColumnTypes.Competition,
              KeywordTableColumnTypes.Cpc,
              KeywordTableColumnTypes.CpcBidLow,
              KeywordTableColumnTypes.CpcBidHigh,
            ].includes(c.column.type))
            return 'Metrics';
          if (
            [
              KeywordTableColumnTypes.VolumeLast12months,
              KeywordTableColumnTypes.VolumeMonthPeak,
              KeywordTableColumnTypes.VolumeQuarterPeak,
            ].includes(c.column.type))
            return 'Seasonal metrics';
          if (!!project?.tagGroups?.length &&
            [
              KeywordTableColumnTypes.TagGroup
            ].includes(c.column.type))
            return 'Tag groups';
          if (project?.progressHasSerp &&
            [
              //KeywordTableColumnTypes.RankStatus,
              KeywordTableColumnTypes.SerpFeatures
            ].includes(c.column.type))
            return 'Other';
          if ([
            KeywordTableColumnTypes.Rank,
          ].includes(c.column.type))
            return 'Domains';
          if ([
            KeywordTableColumnTypes.KeywordSearch,
            KeywordTableColumnTypes.SeedKeyword,
            KeywordTableColumnTypes.SeedType,
          ].includes(c.column.type))
            return 'Keyword search';
          if (!!project?.tagGroups?.length && project.progressHasTags &&
            [
              KeywordTableColumnTypes.TagCount
            ].includes(c.column.type))
            return 'Other';
          if ([
            KeywordTableColumnTypes.ArchiveTime
          ].includes(c.column.type))
            return 'Other';

        })
        .value(),
    [columnState, debouncedSearchState]
  );
  const categories = ['Metrics', 'Seasonal metrics', 'Tag groups', 'Domains', 'Keyword search', 'Translation', 'Other'];

  if (!categories.some((c) => !!hiddenColumns[c]?.length)) return null;

  return (
    <>
      <div
        className={`
        pl-4 pr-2 mt-2 mb-1
        border-b border-t border-voxel-gray-200
        flex flex-row items-center justify-between
      `}
      >
        <div className="py-1.5 pr-8">
          <VoxelText variant="bread-normal" color="slight" noWrap className="!text-[13px]">
            Hidden columns
          </VoxelText>
        </div>
        <VoxelAdvancedTextSearch
          value={searchState}
          //autoFocus
          onChange={(event) => setSearchState(event.target.value)}
          size="xs"
        />
      </div>
      <div className="pb-2">
        {categories
          .filter((category) => !!hiddenColumns[category]?.length)
          .map((category) => (
            <div key={category}>
              {!debouncedSearchState.length && (
                <div
                  className={`pl-4 pr-2 py-1 flex flex-row items-center gap-2 group/hidden cursor-pointer`}
                  onClick={() =>
                    setExpandedGroups((prev) => ({ ...prev, [category]: !prev[category] }))
                  }
                >
                  <FontAwesomeIcon
                    icon={expandedGroups[category] ? faChevronDown : faChevronUp}
                    size="sm"
                    className="text-voxel-gray-400 group-hover/hidden:text-voxel-gray-600"
                  />
                  <VoxelText
                    variant="table"
                    colorClassName={'text-voxel-gray-500 group-hover/hidden:text-voxel-gray-700'}
                  >
                    {category} ({hiddenColumns[category]?.length ?? 0})
                  </VoxelText>
                  <div className="flex-grow" />
                  {!!hiddenColumns[category]?.length && (
                    <VoxelButton
                      size='2xs'
                      title="Add all"
                      className='-m-2 mr-0'
                      onClick={(event) => {
                        event.stopPropagation();
                        event.preventDefault();
                        hiddenColumns[category].forEach((cat) => {
                          handleAdd(cat.column);
                        });
                      }}
                    />
                  )}
                </div>
              )}

              {(!!debouncedSearchState.length || expandedGroups[category]) && (
                <div>
                  {hiddenColumns[category]?.map((column) => (
                    <div
                      key={getColumnId(column.column)}
                      onClick={() => handleAdd(column.column)}
                      className={`
                        cursor-pointer
                        pl-9 pr-6
                        select-none
                        hover:bg-gray-50
                        flex flex-row items-center justify-between
                        group/picker
                      `}
                    >
                      <div className="flex-grow py-[5px]">
                        <VoxelText variant="table" color="dark">
                          {column.name}
                        </VoxelText>
                      </div>
                      <VoxelInfo tooltip={getColumnDescription(column.column, project)} />
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
      </div>
    </>
  );
};

export default KeywordColumnPickerHiddenColumns;
